import { computed, reactive, ref } from "vue";
import { defineStore } from "pinia";
import type { CustomCategoryDto } from "@/api/classes/customCategories/customCategories.dto";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import CUSTOMCATEGORIES_API from "@/api/classes/customCategories/customCategories_api";
import { useTableColumns } from "@/composables/table/tableColumns";
import type { CustomCategoryTableColumnType } from "@/components/customCategories/categoriesList/table/CategoriesTable.vue";
import {
  MarketPlacesEnum,
  type MarketPlacesEnum as MarketPlacesEnumType,
} from "@/types/types";
import { MarketPlace } from "@/types/market-place";
import type { ChestniyZnakCategoryEntity } from "@/types/product/productTypes";

export const useCustomCategoriesStore = defineStore("customCategories", () => {
  const customCategories = reactive<CustomCategoryDto[]>([]);
  const statusLoadGetCustomCategories = ref(enumStatusLoad.NOT_STARTED);
  const statusImportCustomCategories = ref(enumStatusLoad.NOT_STARTED);
  const filterSearch = ref("");
  const columnsConfigName = "baskitCustomCategoriesCols1";

  const getCategoryMarkets = (el: CustomCategoryDto): MarketPlace[] => {
    const markets = [];
    if (el.wildberriesCategoryId) {
      markets.push(MarketPlace.Wildberries);
    }
    if (el.ozonDescriptionCategoryId) {
      markets.push(MarketPlace.Ozon);
    }
    const chZnCategories = (
      el.ozonDescriptionCategoryId
        ? (JSON.parse(el.chestniyZnakCategories) ?? [])
        : []
    )
      .map((el: ChestniyZnakCategoryEntity) => el.cat_id)
      .filter(Boolean);
    if (chZnCategories.length > 0) {
      markets.push(MarketPlace.ChestniyZnak);
    }
    return markets;
  };

  const dataTable = computed(() => {
    return customCategories.map((el) => {
      return {
        ...el,
        name: el.name,
        id: el.id,
        tnvd: el.tnvd,
        paidStorageKgvp: el.wbComission.paidStorageKgvp,
        kgvpMarketplace: el.wbComission.kgvpMarketplace,
        salesPercentFbo: el.ozonComission.salesPercentFbo,
        salesPercentFbs: el.ozonComission.salesPercentFbs,
        action: "",
        markets: getCategoryMarkets(el),
        sizesOn: el.sizesOn,
      };
    });
  });

  const columnForInit = ref<CustomCategoryTableColumnType[]>([
    {
      title: "Название",
      dataIndex: "name",
      width: 150,
      align: "center",
      resizable: true,
      fixed: "left",
    },
    {
      title: "Связка МП",
      dataIndex: "markets",
      align: "center",
      width: 100,
      resizable: true,
    },
    {
      title: "Разделяются по размеру",
      dataIndex: "sizesOn",
      align: "center",
      minWidth: 100,
      width: 100,
      maxWidth: 100,
      resizable: false,
    },
    {
      title: "ТНВЭД",
      dataIndex: "tnvd",
      width: 150,
      align: "center",
      resizable: true,
    },
    {
      title: "FBW",
      dataIndex: "paidStorageKgvp",
      width: 150,
      align: "center",
      resizable: true,
    },
    {
      title: "FBS WB",
      dataIndex: "kgvpMarketplace",
      width: 150,
      align: "center",
      resizable: true,
    },
    {
      title: "FBO",
      dataIndex: "salesPercentFbo",
      width: 150,
      align: "center",
      resizable: true,
    },
    {
      title: "FBS Ozon",
      dataIndex: "salesPercentFbs",
      width: 150,
      align: "center",
      resizable: true,
    },
    {
      title: "Действия",
      dataIndex: "action",
      width: 50,
      align: "center",
      resizable: true,
      fixed: "right",
    },
  ]);

  const customCategoriesFiltered = computed(() => {
    return dataTable.value.filter((el) =>
      el.name.toLowerCase().includes(filterSearch.value.toLowerCase()),
    );
  });

  const customCategoriesOptions = computed(() => {
    return customCategories.map((el) => {
      return {
        value: el.id,
        label: el.name,
      };
    });
  });

  const getCustomCategories = async () => {
    try {
      statusLoadGetCustomCategories.value = enumStatusLoad.LOADING;

      const response = await CUSTOMCATEGORIES_API.getCustomCategories();
      customCategories.length = 0;
      if (response.status === 200) {
        customCategories.push(...response.data);
      }

      statusLoadGetCustomCategories.value = enumStatusLoad.LOADED;
    } catch (error) {
      statusLoadGetCustomCategories.value = enumStatusLoad.ERROR;
      console.error(error);
      throw error;
    }
  };

  const importCategories = async (
    importType: "all" | MarketPlacesEnumType,
    addCharacteristics: boolean,
  ) => {
    try {
      statusImportCustomCategories.value = enumStatusLoad.LOADING;
      await CUSTOMCATEGORIES_API.importCustomCategories({
        addCharacteristics,
        addWb:
          importType === "all" || importType === MarketPlacesEnum.wildberries,
        addOzon: importType === "all" || importType === MarketPlacesEnum.ozon,
      });
      statusImportCustomCategories.value = enumStatusLoad.LOADED;
    } catch (error) {
      statusImportCustomCategories.value = enumStatusLoad.ERROR;
      console.error(error);
    } finally {
      await getCustomCategories();
    }
  };

  const { columnsFiltered, dragList, initDragList } = useTableColumns(
    columnForInit,
    columnsConfigName,
    customCategoriesFiltered,
  );

  function $reset() {
    customCategories.length = 0;
    statusLoadGetCustomCategories.value = enumStatusLoad.NOT_STARTED;
    filterSearch.value = "";
  }

  return {
    customCategories,
    getCustomCategories,
    importCategories,
    statusLoadGetCustomCategories,
    statusImportCustomCategories,
    customCategoriesFiltered,
    filterSearch,
    customCategoriesOptions,
    dataTable,
    columnsFiltered,
    dragList,
    initDragList,
    columnForInit,
    columnsConfigName,
    $reset,
  };
});
